import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent } from "../../../components/ui/card";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Textarea } from "../../../components/ui/textarea";
import { Label } from "../../../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../../components/ui/dialog";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { MKT, getAdminAuth } from "./AdminEmailMarketing";

const STARTERS = [
  { name: "Locksmith Outreach", subject: "{{company_name}} — stop losing locksmith jobs to missed calls", html_content: "<div style=\"font-family:Arial,Helvetica,sans-serif;max-width:560px;margin:0 auto;color:#1f2937;\"><p style=\"font-size:15px;\">Hi {{first_name}},</p><p style=\"font-size:15px;line-height:1.6;\">Most locksmith shops we talk to lose 2–3 jobs a week to the same things: missed calls while out on a job, quotes that never get followed up, and invoices chased for days.</p><p style=\"font-size:15px;line-height:1.6;\"><strong>Fieldz AI</strong> was built for locksmiths like {{company_name}} — dispatch, estimates, invoicing, business texting and call management in one simple app:</p><ul style=\"font-size:14px;line-height:1.8;color:#374151;padding-left:18px;\"><li><strong>Never miss a call</strong> — business phone line with call routing and automatic SMS follow-up</li><li><strong>Quote on the spot</strong> — estimates customers e-sign right from their phone</li><li><strong>Get paid same-day</strong> — invoices with built-in card payment links</li><li><strong>Dispatch techs in seconds</strong> — jobs, schedules and routes in one view</li></ul><p style=\"text-align:center;margin:26px 0;\"><a href=\"https://fieldzai.com\" style=\"background:#0046BE;color:#ffffff;padding:12px 28px;border-radius:8px;text-decoration:none;font-weight:bold;font-size:15px;display:inline-block;\">Start Free — 7-Day Trial</a></p><p style=\"font-size:13px;color:#6b7280;line-height:1.6;\">No credit card needed. If it's not a fit for {{company_name}}, no hard feelings — reply and tell us what would be.</p><p style=\"font-size:14px;\">— The Fieldz AI Team</p></div>" },
  { name: "Fieldz AI Introduction", subject: "A better way to run {{company_name}}", html_content: "<h2>Hi {{first_name}},</h2><p>Running a {{service_type}} business means juggling calls, dispatch, invoices and technicians all day. Fieldz AI puts all of it in one platform.</p><p><a href=\"https://fieldzai.com\" style=\"background:#0046BE;color:#fff;padding:10px 22px;border-radius:8px;text-decoration:none;display:inline-block;\">See how it works</a></p>" },
  { name: "Free Demo Invitation", subject: "Free demo for {{company_name}}", html_content: "<h2>Hi {{first_name}},</h2><p>Want to see how {{service_type}} companies in {{city}} save hours every week? Book a free 15-minute demo of Fieldz AI.</p><p><a href=\"https://fieldzai.com/demo\" style=\"background:#0046BE;color:#fff;padding:10px 22px;border-radius:8px;text-decoration:none;display:inline-block;\">Book a Demo</a></p>" },
  { name: "Follow-Up", subject: "Following up — {{company_name}}", html_content: "<p>Hi {{first_name}},</p><p>Just checking back in — did you get a chance to look at Fieldz AI for {{company_name}}?</p><p><a href=\"https://fieldzai.com\">Take a look here</a></p>" },
];

export default function TemplatesTab() {
  const [templates, setTemplates] = useState([]);
  const [editing, setEditing] = useState(null);

  const fetchTemplates = async () => {
    try {
      const res = await axios.get(`${MKT}/templates`, getAdminAuth());
      setTemplates(res.data.templates);
    } catch (e) { toast.error("Failed to load templates"); }
  };
  useEffect(() => { fetchTemplates(); }, []);

  const save = async () => {
    try {
      if (editing.template_id) {
        await axios.put(`${MKT}/templates/${editing.template_id}`,
          { name: editing.name, subject: editing.subject, html_content: editing.html_content }, getAdminAuth());
      } else {
        await axios.post(`${MKT}/templates`,
          { name: editing.name, subject: editing.subject, html_content: editing.html_content }, getAdminAuth());
      }
      toast.success("Template saved");
      setEditing(null); fetchTemplates();
    } catch (e) { toast.error("Failed to save"); }
  };

  const remove = async (t) => {
    if (!window.confirm(`Delete template "${t.name}"?`)) return;
    await axios.delete(`${MKT}/templates/${t.template_id}`, getAdminAuth());
    fetchTemplates();
  };

  return (
    <div className="space-y-4 mt-4" data-testid="marketing-templates-tab">
      <div className="flex flex-wrap gap-2">
        <Button size="sm" onClick={() => setEditing({ name: "", subject: "", html_content: "" })} data-testid="new-template-btn">
          <Plus className="w-4 h-4 mr-1" />New Template
        </Button>
        {STARTERS.map(s => (
          <Button key={s.name} variant="outline" size="sm" onClick={() => setEditing({ ...s })} data-testid={`starter-${s.name}`}>
            + {s.name}
          </Button>
        ))}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {templates.map(t => (
          <Card key={t.template_id} className="border-border">
            <CardContent className="p-4">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-foreground truncate">{t.name}</p>
                  <p className="text-xs text-muted-foreground truncate">{t.subject || "no subject"}</p>
                </div>
                <div className="flex shrink-0">
                  <Button variant="ghost" size="sm" onClick={() => setEditing({ ...t })}><Pencil className="w-3.5 h-3.5" /></Button>
                  <Button variant="ghost" size="sm" onClick={() => remove(t)}><Trash2 className="w-3.5 h-3.5 text-red-400" /></Button>
                </div>
              </div>
              <div className="mt-2 border border-border rounded p-2 max-h-24 overflow-hidden text-[10px] text-muted-foreground"
                dangerouslySetInnerHTML={{ __html: t.html_content }} />
            </CardContent>
          </Card>
        ))}
        {templates.length === 0 && <p className="text-sm text-muted-foreground col-span-3">No saved templates — start from a starter above.</p>}
      </div>

      <Dialog open={!!editing} onOpenChange={() => setEditing(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="template-editor-dialog">
          <DialogHeader><DialogTitle>{editing?.template_id ? "Edit Template" : "New Template"}</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div><Label>Name</Label><Input value={editing.name} onChange={(e) => setEditing(p => ({ ...p, name: e.target.value }))} data-testid="template-name-input" /></div>
              <div><Label>Subject</Label><Input value={editing.subject} onChange={(e) => setEditing(p => ({ ...p, subject: e.target.value }))} /></div>
              <div>
                <Label>HTML Content</Label>
                <Textarea rows={10} className="font-mono text-xs" value={editing.html_content}
                  onChange={(e) => setEditing(p => ({ ...p, html_content: e.target.value }))} data-testid="template-html-input" />
                <p className="text-[10px] text-muted-foreground mt-1">Variables: {"{{first_name}} {{company_name}} {{service_type}} {{city}} {{state}}"}</p>
              </div>
              <div>
                <Label className="text-xs">Preview</Label>
                <div className="border border-border rounded p-3 bg-white text-black text-sm max-h-56 overflow-y-auto"
                  dangerouslySetInnerHTML={{ __html: editing.html_content }} />
              </div>
              <Button onClick={save} disabled={!editing.name.trim()} data-testid="save-template-btn">Save Template</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
